import { HolderOutlined } from "@ant-design/icons";
import { useSortable } from "@dnd-kit/react/sortable";
import { Select } from "antd";
import React, { useMemo } from "react";
import InputDuration from "../../../../../components/Basic/InputDuration";
import Icon, { ICON_NAME } from "../../../../../components/Icon";
import Menu from "../../../../../components/Menu";
import { openNote } from "../../../../../components/Popup/PopupOpNote";
import { renderOpts } from "../../../../../util/varibles/global";
import { IServiceTask, ITaskService } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import ErrorOfOperation, { ErrorName } from "../_components/ErrorOfOperation";
import { useEditOp } from "../EditOperationContext";
import styles from "./style.module.scss";

interface IProps {
    data: ITaskService
    index: number
    onChange(value: Partial<ITaskService>): void
    onDelete(): void
}

const Task: React.FC<IProps> = ({ data, index, onChange, onDelete }) => {
    const [{ loading, data: serviceTasks }] = useEditOp(state => state.serviceTasks)
    const [units] = useEditOp(state => state.units)
    const { sub_operations, error = {} } = data;

    const { isDragging, ref, handleRef } = useSortable({
        id: data.group_id,
        index,
    });

    const taskId = useMemo(() => serviceTasks.some(item => item.id === data.type) ? data.type : undefined, [serviceTasks, data.type])

    return <div
        ref={ref}
        className={styles.sortableTask}
        data-shadow={isDragging || undefined}
    >
        <div className={styles.task}>
            <div className="flex">
                <div className="bt-ghost cursor-grab" data-icon="true" ref={handleRef}>
                    <HolderOutlined />
                </div>
            </div>
            <Select
                className='select-mode-tag'
                variant="borderless"
                mode="multiple"
                placeholder="Units"
                value={sub_operations.map(item => item.id)}
                options={Object.keys(units).map(key => ({ value: key, label: units[key].unit_id }))}
                onSelect={(_, opt) => onChange({
                    sub_operations: [...sub_operations, {
                        id: opt.value,
                        unit_id: opt.label
                    }]
                })}
                onDeselect={value => onChange({ sub_operations: sub_operations.filter(item => item.id !== value) })}
            />
            <Select
                variant="borderless"
                value={taskId}
                placeholder="Task"
                loading={loading}
                onChange={(_, opt) => {
                    if (!opt || Array.isArray(opt)) return;
                    const { type: __, ...args } = error;
                    const { id, name, duration } = opt.root;
                    const value: Partial<ITaskService> = { type: id, name, error: args };
                    if (duration) {
                        const { [ErrorName.Duration]: _, ...subArgs } = value.error;
                        value.error = subArgs;
                        value.duration = duration
                    }

                    onChange(value)
                }}
                options={renderOpts<IServiceTask>(serviceTasks)}
            />
            <InputDuration
                value={data.duration}
                onError={isEmpty => {
                    onChange({
                        error: {
                            ...error, [ErrorName.Duration]: {
                                message: isEmpty ? notify[NotifyCode.E29](['Duration']) : notify[NotifyCode.E19]()
                            }
                        }
                    })
                }}
                onChange={value => {
                    if (value !== data.duration) {
                        const endValue: Partial<ITaskService> = { duration: value };
                        if (value && value > 0) {
                            const { [ErrorName.Duration]: _, ...args } = error;
                            endValue.error = args;
                        }
                        onChange(endValue)
                    }
                }} />
            <div className="flex items-center justify-end gap-1">
                <div>
                    {data.note}
                </div>
                <div className="bt-ghost w-[32px]" data-icon="true" onClick={() => openNote({ note: data.note || "", title: "Task", onSave: (note) => onChange({ note }) })}>
                    <Icon icon={ICON_NAME.NOTE} />
                </div>
            </div>
            <div className="flex justify-end">
                <Menu
                    placement="bottomRight"
                    items={[{
                        key: 'delete',
                        onClick: onDelete,
                        label: <div className='menu-line' data-danger='true'>
                            <Icon icon={ICON_NAME.DELETE} danger />
                            <span>Delete task</span>
                        </div>
                    }]}>
                    <div className='three-dot' />
                </Menu>
            </div>
        </div>
        <ErrorOfOperation error_detail={error} />
    </div>;
};

export default Task;
